import React, { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { TaskyAlert, TaskyBadge, TaskyButton, useConfirm } from '../tasky';
import adminVideoStyleRecipeService, { type AdminVideoStyleRecipe, videoStyleErrorMessages } from '../../services/api/adminVideoStyleRecipeService';

interface Props {
  recipe: AdminVideoStyleRecipe;
  onChange: (recipe: AdminVideoStyleRecipe) => void;
}

/**
 * スタイル詳細の「見本」: ユーザーのスタイル選択のカードに出る静止画と音なしのダイジェスト。
 * 見本は完成した試作の「このスタイルの見本にする」で作る（作成は非同期。作成中の取り直しは親が行う）。
 */
const VideoStyleSampleSection: React.FC<Props> = ({ recipe, onChange }) => {
  const confirm = useConfirm();
  const [errors, setErrors] = useState<string[]>([]);
  const processing = recipe.sample_status === 'processing';
  const hasSample = Boolean(recipe.sample_video_url || recipe.thumbnail_url);

  const remove = async () => {
    if (!(await confirm({ title: '見本を外しますか？', description: 'スタイル選択のカードはアイコンに戻ります。', confirmText: '外す', tone: 'destructive' }))) return;
    setErrors([]);
    try {
      onChange(await adminVideoStyleRecipeService.removeSample(recipe.id));
    } catch (error) {
      setErrors(videoStyleErrorMessages(error, '見本を外せませんでした。'));
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="video-style-sample-section">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium">見本</p>
        {processing && (
          <TaskyBadge variant="info" dot>
            作成中
          </TaskyBadge>
        )}
        {recipe.sample_status === 'failed' && <TaskyBadge variant="destructive">失敗</TaskyBadge>}
      </div>
      <p className="text-xs text-gray-500">スタイル選択でこのスタイルを選ぶ全ユーザーに表示されます。完成した試作の「このスタイルの見本にする」で作ります。</p>
      {recipe.sample_status === 'failed' && recipe.sample_error && <TaskyAlert tone="error">{recipe.sample_error}</TaskyAlert>}
      {errors.length > 0 && (
        <TaskyAlert tone="error">
          {errors.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </TaskyAlert>
      )}
      <div className="flex items-end gap-3">
        {hasSample ? (
          <div className="w-28 overflow-hidden rounded-tasky-sm border border-warm-border bg-warm-surface">
            {recipe.sample_video_url ? (
              <video
                src={recipe.sample_video_url}
                poster={recipe.thumbnail_url ?? undefined}
                muted
                loop
                autoPlay
                playsInline
                aria-label="見本のダイジェスト"
                className="block aspect-[9/16] w-full object-cover"
              />
            ) : (
              <img src={recipe.thumbnail_url ?? undefined} alt="見本の静止画" className="block aspect-[9/16] w-full object-cover" />
            )}
          </div>
        ) : (
          !processing && <p className="text-sm text-gray-500">見本はまだありません（カードはアイコンで出ます）。</p>
        )}
        {/* 作成中に外すと出来たものは使わない（止まったままの作成中もここで解ける） */}
        {(hasSample || recipe.sample_status !== null) && (
          <TaskyButton size="sm" variant="ghost" onClick={() => void remove()}>
            <ImageOff aria-hidden />
            見本を外す
          </TaskyButton>
        )}
      </div>
    </div>
  );
};

export default VideoStyleSampleSection;
